import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  Tag,
  Text,
  useBreakpointValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { isValidUSPhoneNumber } from '@utils/validatePhone';
import React, { useEffect, useRef, useState } from 'react';
import { FiRefreshCw, FiSend } from 'react-icons/fi';
import ChatMessage from './ChatMessage';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  isLoading?: boolean;
  role?: 'user' | 'assistant';
  showContactButtons?: boolean;
}

interface MedicareChatInterfaceProps {
  dynamicPhoneNumber: string;
}

const examplePrompts = [
  'What are the different parts of Medicare?',
  'How do I enroll in Medicare?',
  'What is the cost of Medicare Part B?',
  'When is the Medicare open enrollment period?',
];

const MedicareChatInterface: React.FC<MedicareChatInterfaceProps> = ({ dynamicPhoneNumber }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const [collectingPhoneNumberFor, setCollectingPhoneNumberFor] = useState<{
    aiPromptMessageId: string;
    contactType: 'call' | 'text';
  } | null>(null);
  const [phoneNumberInput, setPhoneNumberInput] = useState('');
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);

  const placeholderText = useBreakpointValue({
    base: 'Type here...',
    md: 'Type your question here...',
  });

  const sendButtonText = useBreakpointValue({
    base: '',
    md: 'Send',
  });

  const initialChatHeight = '200px';
  const activeChatHeight = '450px';

  const scrollToBottom = (forceScroll = false) => {
    const container = messagesContainerRef.current;
    if (container) {
      const isScrolledToBottom = container.scrollHeight - container.clientHeight <= container.scrollTop + 100;
      if (forceScroll || isScrolledToBottom) {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  useEffect(() => {
    scrollToBottom(true);
  }, [messages.length]);

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender === 'ai' && !lastMessage.isLoading) {
        scrollToBottom(true);
      }
    }
  }, [messages]);

  useEffect(() => {
    if (collectingPhoneNumberFor && phoneNumberInputRef.current) {
      setTimeout(() => phoneNumberInputRef.current?.focus(), 0);
    }
  }, [collectingPhoneNumberFor]);

  const handleContactPreferenceSelected = (clickedMessageId: string, contactType: 'call' | 'text') => {
    const aiPromptText =
      contactType === 'call'
        ? 'Great! To arrange your call, please enter your phone number below.'
        : 'Great! To arrange your text message, please enter your phone number below.';

    const newAiPromptMessageId = Date.now().toString();

    setMessages((prevMessages) => {
      const updatedMessages = prevMessages.map((msg) =>
        msg.id === clickedMessageId ? { ...msg, showContactButtons: false } : msg
      );
      const aiPromptMessage: Message = {
        id: newAiPromptMessageId,
        text: aiPromptText,
        sender: 'ai',
        isLoading: false,
        role: 'assistant',
        showContactButtons: false,
      };
      return [...updatedMessages, aiPromptMessage];
    });

    setCollectingPhoneNumberFor({ aiPromptMessageId: newAiPromptMessageId, contactType });
    setPhoneNumberInput('');
  };

  const handleSendMessage = async (textToSend?: string) => {
    const currentMessageText = textToSend || inputValue.trim();
    if (!currentMessageText) return;

    setMessages((prevMessages) => prevMessages.map((msg) => ({ ...msg, showContactButtons: false })));

    const userMessage: Message = {
      id: Date.now().toString(),
      text: currentMessageText,
      sender: 'user',
      role: 'user',
    };

    const aiMessageId = (Date.now() + 1).toString();
    const initialAiMessage: Message = {
      id: aiMessageId,
      text: '',
      sender: 'ai',
      role: 'assistant',
      isLoading: true,
    };
    setMessages((prev) => [...prev, userMessage, initialAiMessage]);

    if (!textToSend) {
      setInputValue('');
    }
    setIsLoading(true);

    try {
      const apiMessages = [...messages, userMessage].map((msg) => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
      }));

      const response = await fetch('/api/medicare-chat', {
        // MODIFIED API ROUTE
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages, dynamicPhoneNumber }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'API request failed' }));
        throw new Error(errorData.error || `API request failed: ${response.status}`);
      }
      if (!response.body) throw new Error('Response body is null');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let contactOfferDetectedInStream = false;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        accumulatedText += decoder.decode(value, { stream: true });
        if (accumulatedText.includes('[CONTACT_OFFER]')) {
          contactOfferDetectedInStream = true;
        }
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === aiMessageId
              ? { ...msg, text: accumulatedText.replace('[CONTACT_OFFER]', ''), isLoading: true }
              : msg
          )
        );
        scrollToBottom();
      }

      let finalDisplayMessage = accumulatedText;
      let shouldShowButtonsForThisMessage = false;
      if (contactOfferDetectedInStream || accumulatedText.includes('[CONTACT_OFFER]')) {
        shouldShowButtonsForThisMessage = true;
        finalDisplayMessage = accumulatedText.replace('[CONTACT_OFFER]', '').trim();
      }

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === aiMessageId
            ? {
                ...msg,
                text: finalDisplayMessage,
                isLoading: false,
                showContactButtons: shouldShowButtonsForThisMessage,
              }
            : { ...msg, showContactButtons: false }
        )
      );
    } catch (error) {
      console.error('Error sending message or processing stream:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === aiMessageId ? { ...msg, text: `Error: ${errorMessage}`, isLoading: false } : msg
        )
      );
      toast({
        title: 'Error',
        description: `Failed to get response from AI: ${errorMessage}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setInputValue('');
    toast({
      title: 'New chat started.',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSubmitPhoneNumber = () => {
    const trimmedPhoneNumber = phoneNumberInput.trim();

    if (!trimmedPhoneNumber) {
      toast({
        title: 'Phone number required',
        description: 'Please enter your phone number.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!isValidUSPhoneNumber(trimmedPhoneNumber)) {
      toast({
        title: 'Invalid phone number format',
        description: 'Please enter a valid 10-digit US phone number.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const contactType = collectingPhoneNumberFor?.contactType;
    const finalConfirmationText =
      contactType === 'call'
        ? `Thank you! We've received your number (${trimmedPhoneNumber}) and will call you shortly.`
        : `Thank you! We've received your number (${trimmedPhoneNumber}) and will text you shortly.`;

    const finalAiMessage: Message = {
      id: Date.now().toString(),
      text: finalConfirmationText,
      sender: 'ai',
      isLoading: false,
      role: 'assistant',
      showContactButtons: false,
    };

    setMessages((prevMessages) => [...prevMessages, finalAiMessage]);
    setCollectingPhoneNumberFor(null);
    setPhoneNumberInput('');

    fetch('/api/notify-slack-contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phoneNumber: trimmedPhoneNumber,
        contactPreference: contactType,
        chatbotType: 'Medicare',
        source: 'Medicare Chatbot',
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'API request failed with no JSON body' }));
          throw new Error(`API request failed: ${response.status} - ${errorData?.error || response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log('Slack notification API call successful:', data?.message);
        toast({
          title: 'Contact preference submitted!',
          description: `We will be in touch via ${contactType} shortly.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      })
      .catch((error) => {
        console.error('Error calling Slack notification API:', error);
        toast({
          title: 'Submission warning',
          description: 'There was an issue notifying our team, but your request is logged.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      });
  };

  return (
    <VStack
      spacing={6}
      align="stretch"
      width="100%"
      borderWidth="1px"
      borderRadius="lg"
      py={6}
      px={{ base: 0, md: 6 }}
      boxShadow="md"
    >
      <Flex justifyContent="space-between" alignItems="center" mb={2} px={{ base: 4, md: 0 }}>
        <Text fontWeight="bold" fontSize="xl">
          Medicare Assistant
        </Text>
        <Button
          leftIcon={<Icon as={FiRefreshCw} fontSize={{ base: 'md', md: 'lg' }} />}
          onClick={handleNewChat}
          size={{ base: 'sm', md: 'sm' }}
          variant="outline"
          colorScheme="purple"
        >
          New Chat
        </Button>
      </Flex>

      <VStack
        ref={messagesContainerRef}
        spacing={4}
        height={messages.length === 0 ? initialChatHeight : activeChatHeight}
        minHeight={initialChatHeight}
        overflowY="auto"
        py={{ base: 1, md: 4 }}
        px={{ base: 0, md: 4 }}
        borderWidth="1px"
        borderRadius="md"
        bg="gray.50"
        justifyContent={messages.length === 0 ? 'center' : 'flex-start'}
        transition="height 0.3s ease-in-out"
      >
        {messages.length === 0 && (
          <Box textAlign="center" px={4}>
            <Text fontSize="xl" fontWeight="medium" color="gray.700">
              Ask a question about Medicare.
            </Text>
            <Text fontSize="md" color="gray.500" mt={2}>
              Or click on a common question below.
            </Text>
          </Box>
        )}
        {messages.map((msg) => (
          <React.Fragment key={msg.id}>
            <ChatMessage
              message={msg}
              onPreferCall={msg.showContactButtons ? () => handleContactPreferenceSelected(msg.id, 'call') : undefined}
              onPreferText={msg.showContactButtons ? () => handleContactPreferenceSelected(msg.id, 'text') : undefined}
            />
            {collectingPhoneNumberFor && collectingPhoneNumberFor.aiPromptMessageId === msg.id && msg.sender === 'ai' && (
              <Box
                pl={{ base: 'calc(24px + 8px + 8px)', md: 'calc(32px + 12px + 16px)' }}
                pr={{ base: 2, md: 4 }}
                pb={3}
                pt={2}
                w="100%"
                maxW={{ base: '95%', md: '80%' }}
                ml="auto"
                mr="auto"
                alignSelf={'flex-start'}
              >
                <HStack
                  as="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmitPhoneNumber();
                  }}
                >
                  <Input
                    ref={phoneNumberInputRef}
                    placeholder="Enter phone number"
                    value={phoneNumberInput}
                    onChange={(e) => setPhoneNumberInput(e.target.value)}
                    type="tel"
                    size="md"
                  />
                  <Button type="submit" colorScheme="purple" size="md">
                    Submit
                  </Button>
                </HStack>
              </Box>
            )}
          </React.Fragment>
        ))}
      </VStack>

      <Box px={{ base: 4, md: 0 }}>
        <Text fontSize="md" color="gray.600" mb={3}>
          Common questions:
        </Text>
        <Flex wrap="wrap" gap={3}>
          {examplePrompts.map((prompt) => (
            <Tag
              key={prompt}
              size="lg"
              variant="subtle"
              colorScheme="cyan"
              onClick={() => {
                if (!isLoading) handleSendMessage(prompt);
              }}
              cursor={isLoading ? 'not-allowed' : 'pointer'}
              _hover={isLoading ? {} : { bg: 'cyan.100' }}
              style={isLoading ? { opacity: 0.6 } : {}}
              fontSize="15px"
            >
              {prompt}
            </Tag>
          ))}
        </Flex>
      </Box>

      <HStack
        as="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
        spacing={3}
        px={{ base: 4, md: 0 }}
      >
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholderText}
          isDisabled={isLoading}
          size="lg"
          fontSize="md"
        />
        <Button
          leftIcon={sendButtonText ? <Icon as={FiSend} fontSize={{ base: 'md', md: 'lg' }} /> : undefined}
          type="submit"
          colorScheme="purple"
          isLoading={isLoading}
          isDisabled={isLoading || !inputValue.trim()}
          size={{ base: 'md', md: 'lg' }}
          px={{ base: 3, md: 6 }}
          aria-label="Send message"
        >
          {sendButtonText ? sendButtonText : <Icon as={FiSend} fontSize="md" />}
        </Button>
      </HStack>
    </VStack>
  );
};

export default MedicareChatInterface;
